import { format, isBefore, isValid, parseISO } from "date-fns";

import { validationNames, statuses, imageDelivery } from "../constants/listings";
import { statusesConstant, statusesConstantKeys } from "../constants/referralRequestDetail";
import { WEBSITE, phoneE164 } from "../constants/regex";
import visaIcon from "../media/icons/visa-logo.svg";
import americanExprIcon from "../media/icons/american-express.svg";
import masterCardBlack from "../media/icons/mastercard-black.svg";
import discoverIcon from "../media/icons/discover-logo-black.svg";
import genericCardIcon from "../media/icons/generic-card.svg";

export const getStateName = (stateList, stateCode) => {
  return stateList?.find(state => state.state === stateCode)?.title;
};

export const disableScroll = () => {
  document.body.classList.add("disableScroll");
};

export const enableScroll = () => {
  document.body.classList.remove("disableScroll");
};

export const formatDuration = duration => {
  if (duration === "0") {
    return "-";
  }

  const d = new Date(duration * 1000);
  const m = d.getUTCMinutes();
  const s = d.getUTCSeconds();
  return m ? `${m}m ${s}s` : `${s}s`;
};

/**
 * Parse a timestamp coming from the API into a Date.
 *
 * - ISO-8601 with an offset resolves to the correct instant in the viewer's timezone.
 * - The legacy MySQL DATETIME (`YYYY-MM-DD HH:mm:ss`, no offset) is read as local time. Kept so a
 *   frontend deploy landing ahead of the API change, and cached responses, still render.
 *
 * date-fns parseISO does the parsing, never the engine's Date parser, so the space-separated
 * form that Safari's `new Date()` rejects needs no rewriting: parseISO accepts a space or `T`.
 *
 * Returns null rather than an Invalid Date, so callers render a placeholder instead of throwing
 * out of date-fns format() mid-render.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export const parseApiDate = value => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  const parsed = parseISO(String(value).trim());

  return isValid(parsed) ? parsed : null;
};

/**
 * format() over parseApiDate(): the fallback instead of a RangeError when the value is missing
 * or unparseable.
 *
 * @param {string|Date|null|undefined} value
 * @param {string} pattern date-fns format pattern
 * @param {string} [fallback]
 * @returns {string}
 */
export const formatApiDate = (value, pattern, fallback = "") => {
  const parsed = parseApiDate(value);

  return parsed ? format(parsed, pattern) : fallback;
};

export const getPracticeById = (practiceList, practiceId, returnFull = false) => {
  const area = practiceList?.find(({ id }) => String(id) === String(practiceId));
  return returnFull ? area : area?.title;
};

export const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});

export const priceFormatterCurrency = currency =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD"
  });

/**
 * An API amount and its ISO 4217 currency code as money: "$1,250.00".
 *
 * Both arrive unformatted and unvalidated (a DECIMAL string and the CHAR(3) column), so this never throws out of a render:
 *
 * - A missing or non-numeric amount returns "", never "NaN".
 * - A missing currency is USD, as in priceFormatterCurrency.
 * - A malformed code (Intl.NumberFormat throws a RangeError) is written beside the amount: "XX 25.00".
 *
 * @param {string|number|null|undefined} amount
 * @param {string|null|undefined} currency
 * @returns {string}
 */
export const formatCurrencyAmount = (amount, currency) => {
  // String(null) and String(undefined) are not numbers, so a missing amount falls out below with the unparseable ones.
  const text = String(amount).trim();
  const numericAmount = Number(text);

  if (text === "" || !Number.isFinite(numericAmount)) {
    return "";
  }

  const code = String(currency ?? "").trim();

  try {
    return priceFormatterCurrency(code).format(numericAmount);
  } catch {
    return `${code.toUpperCase()} ${numericAmount.toFixed(2)}`;
  }
};

export const getIsStateSelectionValid = (item, itemCountryFieldName, itemStateFieldName) => {
  const state = item[itemStateFieldName];
  const country = item[itemCountryFieldName];
  const countryOfState = state.includes("-") ? state.split("-")[0] : "US";
  return !(state !== "" && country !== countryOfState);
};

export const socialsToObject = (socials, isNew) => {
  const accumulator = {};
  return socials.reduce((obj, item) => {
    return Object.assign(obj, {
      [item.social_name]: {
        social_name: item.social_name,
        social_url: item.social_url,
        ...(item.prkey !== undefined ? { prkey: item.prkey } : {}),
        ...(isNew ? { isNew: true } : {})
      }
    });
  }, accumulator);
};

export const socialsToArray = socials => {
  const socialsArray = [];
  const entries = Object.entries(socials);
  entries.forEach(entry => socialsArray.push(entry[1]));
  return socialsArray;
};

// eslint-disable-next-line
export const detectDuplicateEntry = (matchParam, arrayCurrent, editedItem) => {
  return arrayCurrent.some(item => item[matchParam] === editedItem[matchParam]);
};

export const detectDuplicateEntryPractices = (matchParam, arrayCurrent, editedItem) => {
  return arrayCurrent.some(item => String(item[matchParam]) === String(editedItem));
};

export const detectDuplicateAllFields = (arrayCurrent, newItem, index) => {
  const newItemKeys = Object.keys(newItem);

  const findDuplicate = arrayCurrent.filter((item, indexItem) => {
    return newItemKeys.every(newItemKey => {
      return item[newItemKey] === newItem[newItemKey] && indexItem !== index;
    });
  });

  return Boolean(findDuplicate?.length);
};

export const normalizeFollowupQuestionOptions = (answer, isValue) => {
  const { answer_options, lead_followup_id } = answer;

  if (answer_options == null || answer_options === "") {
    return [];
  }

  let parsedOptions;
  try {
    parsedOptions = typeof answer_options === "string" ? JSON.parse(answer_options) : answer_options;
  } catch {
    return [];
  }

  if (parsedOptions == null) {
    return [];
  }

  const answerOptions = parsedOptions?.options ?? (Array.isArray(parsedOptions) ? parsedOptions : null);

  if (Array.isArray(answerOptions)) {
    return answerOptions.map((option, index) => ({
      answer: isValue ? option : index.toString(),
      label: option,
      lead_followup_id
    }));
  }

  if (answerOptions && typeof answerOptions === "object") {
    return Object.keys(answerOptions).map((option, index) => ({
      answer: isValue ? option : index.toString(),
      label: answerOptions[option],
      lead_followup_id
    }));
  }

  return [];
};

export const paginate = (source, itemsPerPage = 25, currentPage) => {
  const offset = currentPage * itemsPerPage;
  return source.slice(offset).slice(0, itemsPerPage);
};

export const getFormattedDateTimeZone = str => {
  const currentDate = new Date();
  const timeZone = currentDate.getTimezoneOffset() / 60;

  const date = new Date(str);
  return new Date(date.setHours(date.getHours() + -timeZone));
};

export const getQueryParams = str => {
  return str
    .slice(1)
    .split("&")
    .reduce((total, current) => {
      const array = current.split("=");
      return {
        ...total,
        [array[0]]: array[1]
      };
    }, {});
};

/**
 * Same-origin relative path for post-login redirects. Rejects open redirects
 * (protocol-relative //…, absolute URLs, empty).
 * @param {string|null|undefined} returnPath
 * @param {string} [fallback="/"]
 * @returns {string}
 */
export const getSafeReturnPath = (returnPath, fallback = "/") => {
  if (typeof returnPath !== "string" || returnPath === "") {
    return fallback;
  }
  // queryString.parse already decodes; do not decodeURIComponent again
  // (paths with literal % would otherwise break).
  if (!returnPath.startsWith("/") || returnPath.startsWith("//")) {
    return fallback;
  }
  return returnPath;
};

export const getNameWebsiteFromUrl = (url = "") => {
  return url.replace(WEBSITE, "").split("/")[0];
};

export const filterBySearchValue = (questionsArr, searchValue) => {
  return questionsArr?.filter(question => {
    const questionTitle = question.title.toLowerCase();
    const questionSummary = question.summary.toLowerCase();
    return questionSummary.indexOf(searchValue.toLowerCase()) !== -1 || questionTitle.indexOf(searchValue.toLowerCase()) !== -1;
  });
};

export const filterBookmarked = (isSaved, array) => (!isSaved ? array : array.filter(({ saved }) => String(saved) === "1"));

export const getValidationData = (validations, name) => {
  if (!validations.length) return {};

  return validations.find(({ title }) => title.toLowerCase() === name.toLowerCase()) || {};
};

export const getHiddenSectionValue = (hiddenSection, section_id) => {
  return hiddenSection?.some(({ listing_section_id }) => listing_section_id === section_id) || false;
};

/**
 * Maps validation names to their corresponding section/tab names
 */
const getSectionNameForValidation = validationName => {
  const sectionMap = {
    [validationNames.pictures]: "Details",
    [validationNames.coverImage]: "Details",
    [validationNames.additionalImages]: "Details",
    [validationNames.name]: "Bio",
    [validationNames.bio]: "Bio",
    [validationNames.locations]: "Location",
    [validationNames.website]: "Details",
    [validationNames.specialties]: "Details",
    [validationNames.language]: "Details",
    [validationNames.licenses]: "Licenses",
    [validationNames.educations]: "Details",
    [validationNames.associations]: "Details",
    [validationNames.socialMedia]: "Details",
    [validationNames.video]: "Details",
    [validationNames.tags]: "Details",
    [validationNames.communities]: "Details",
    [validationNames.job]: "Details",
    [validationNames.calendar]: "Details",
    [validationNames.events]: "Details",
    [validationNames.parentOrganization]: "Details"
  };
  return sectionMap[validationName] || "Details";
};

/**
 * Gets user-friendly field names for validation names
 */
const getFieldDisplayName = validationName => {
  const fieldNameMap = {
    [validationNames.pictures]: "Profile Photo",
    [validationNames.coverImage]: "Cover Image",
    [validationNames.additionalImages]: "Additional Images",
    [validationNames.name]: "Name",
    [validationNames.bio]: "Bio/Description",
    [validationNames.locations]: "Location",
    [validationNames.website]: "Website",
    [validationNames.specialties]: "Specialties/Practices",
    [validationNames.language]: "Languages",
    [validationNames.licenses]: "Licenses",
    [validationNames.educations]: "Education",
    [validationNames.associations]: "Associations",
    [validationNames.socialMedia]: "Social Media",
    [validationNames.video]: "Intro Video",
    [validationNames.tags]: "Tags",
    [validationNames.communities]: "Communities/Audiences",
    [validationNames.job]: "Job Information",
    [validationNames.calendar]: "Calendar/Booking",
    [validationNames.events]: "Events Schedule",
    [validationNames.parentOrganization]: "Parent Organization"
  };
  return fieldNameMap[validationName] || validationName;
};

/**
 * Editor sections whose title never reads `custom_name` (eventsSchedules hard-codes
 * "Events Schedule", socials uses an i18n key), so the list keeps the generic label to match.
 */
const fixedTitleFields = [validationNames.events, validationNames.socialMedia];

/**
 * Label for a missing required field: the admin-set `custom_name` the editor shows for the
 * listing type (e.g. "Event Logo"), falling back to the generic map when it is empty.
 * Mirrors RequiredSectionsGate: COALESCE(NULLIF(custom_name, ''), title), except for
 * fixedTitleFields, which follow the editor's title instead.
 */
const getMissingFieldLabel = (validations, validationName) => {
  if (fixedTitleFields.includes(validationName)) return getFieldDisplayName(validationName);

  const customName = getValidationData(validations, validationName).custom_name;
  const trimmed = typeof customName === "string" ? customName.trim() : "";
  return trimmed || getFieldDisplayName(validationName);
};

// all commented code we can use in the future, if not, we will remove it after the release
export const getFillingFields = ({ validations, memberData }) => {
  const {
    cover_image_url: defaultImage,
    attorney,
    description,
    // title,
    practices,
    languages,
    website,
    lawfirms,
    licenses,
    educations,
    associations,
    socials,
    primary_image_url: socialPhoto,
    intro_video: introVideo,
    parent_company: parentCompany,
    tags,
    event_schedules: eventSchedules,
    jobs,
    communities,
    bookmeetings,
    text_langs: textLangs,
    text_lang: textLang
  } = memberData;

  if (!validations.length) {
    return { hasMissingFields: false, missingFields: [], missingSections: [] };
  }

  const missingFields = [];
  const missingSectionsSet = new Set();

  // Note: we intentionally do not treat `directories_availability` as a publish-blocking required field here.
  // const isEmptyDescription = description.includes("<p></p>") && description?.length === 8 ? true : !description?.length;
  const isEmptyBookMeetings = getValidationData(validations, validationNames.calendar).required && !bookmeetings?.length;
  const isEmptyCommunities = getValidationData(validations, validationNames.communities).required && !communities?.length;
  const isEmptyJobs = getValidationData(validations, validationNames.job).required && !jobs?.length;
  const isEmptyTags = getValidationData(validations, validationNames.tags).required && !tags?.length;
  const isEmptyEventSchedules = getValidationData(validations, validationNames.events).required && !eventSchedules?.length;
  const isEmptyVideo = getValidationData(validations, validationNames.video).required && !introVideo?.length;
  const parentOrganizationValidation = getValidationData(validations, validationNames.parentOrganization);
  const parentCompanyData = Array.isArray(parentCompany) ? parentCompany[0] || {} : parentCompany;
  // Publish gate: name-presence only. Six-field required set is enforced by the Formik validator.
  const parentCompanyExists =
    parentCompanyData &&
    Object.keys(parentCompanyData).length > 0 &&
    parentCompanyData.name &&
    typeof parentCompanyData.name === "string" &&
    parentCompanyData.name.trim().length > 0;
  // Only check required flag, not enabled - enabled just means the field is visible/available
  const isParentOrganizationRequired = parentOrganizationValidation.required;
  const isEmptyParentCompany = isParentOrganizationRequired && !parentCompanyExists;
  // Parent organization logo is non-required - only check defaultImage (main listing cover image)
  const picturesHasImage = defaultImage;
  const isEmptyPictures = getValidationData(validations, validationNames.pictures).required && !picturesHasImage;
  const isEmptyPractices = getValidationData(validations, validationNames.specialties).required && !practices?.length;
  const isEmptyLanguages = getValidationData(validations, validationNames.language).required && !languages?.length;
  const textLangData = textLangs || textLang;
  // Mirrors RequiredSectionsGate::hasName — a name in ANY content set satisfies the
  // requirement; `attorney` is the legacy fallback for listings with no text_langs rows.
  const nameValidation = getValidationData(validations, validationNames.name);
  const hasNameValue =
    (Array.isArray(textLangData) &&
      textLangData.some(lang => lang && typeof lang.list_name === "string" && lang.list_name.trim().length > 0)) ||
    Boolean(attorney && String(attorney).trim().length);
  const isEmptyName = nameValidation.required && !hasNameValue;
  const bioValidation = getValidationData(validations, validationNames.bio);
  const normalizedDescription =
    typeof description === "string"
      ? description
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;/g, " ")
          .trim()
      : "";
  const hasBioDescription =
    (textLangData &&
      Array.isArray(textLangData) &&
      textLangData.length > 0 &&
      textLangData.some(lang => lang && lang.list_desc && typeof lang.list_desc === "string" && lang.list_desc.trim().length > 0)) ||
    Boolean(normalizedDescription.length);
  const isEmptyBio = bioValidation.required && !hasBioDescription;
  // const isEmptyBusiness = getValidationData(validations, validationNames.business).required && !title?.length;
  const isEmptyWebsite = getValidationData(validations, validationNames.website).required && !website?.length;
  const isEmptySocations = getValidationData(validations, validationNames.locations).required && !lawfirms?.length;
  const isEmptyLicenses = getValidationData(validations, validationNames.licenses).required && !licenses?.length;
  const isEmptyEducations = getValidationData(validations, validationNames.educations).required && !educations?.length;
  const isEmptyAssociations = getValidationData(validations, validationNames.associations).required && !associations?.length;
  const isEmptySocialMedia = getValidationData(validations, validationNames.socialMedia).required && !socials?.length;
  const isEmptySocialPhoto = getValidationData(validations, validationNames.coverImage).required && !socialPhoto;

  // Collect missing fields with their section information
  if (isEmptyPictures) {
    missingFields.push({
      field: validationNames.pictures,
      displayName: getMissingFieldLabel(validations, validationNames.pictures),
      section: getSectionNameForValidation(validationNames.pictures)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.pictures));
  }
  if (isEmptySocialPhoto) {
    missingFields.push({
      field: validationNames.coverImage,
      displayName: getMissingFieldLabel(validations, validationNames.coverImage),
      section: getSectionNameForValidation(validationNames.coverImage)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.coverImage));
  }
  if (isEmptyEventSchedules) {
    missingFields.push({
      field: validationNames.events,
      displayName: getMissingFieldLabel(validations, validationNames.events),
      section: getSectionNameForValidation(validationNames.events)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.events));
  }
  if (isEmptyParentCompany) {
    missingFields.push({
      field: validationNames.parentOrganization,
      displayName: getMissingFieldLabel(validations, validationNames.parentOrganization),
      section: getSectionNameForValidation(validationNames.parentOrganization)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.parentOrganization));
  }
  if (isEmptyName) {
    missingFields.push({
      field: validationNames.name,
      displayName: getMissingFieldLabel(validations, validationNames.name),
      section: getSectionNameForValidation(validationNames.name)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.name));
  }
  if (isEmptyBio) {
    missingFields.push({
      field: validationNames.bio,
      displayName: getMissingFieldLabel(validations, validationNames.bio),
      section: getSectionNameForValidation(validationNames.bio)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.bio));
  }
  // Note: intentionally not treating `directories_availability` as a publish-blocking required field here.
  if (isEmptyVideo) {
    missingFields.push({
      field: validationNames.video,
      displayName: getMissingFieldLabel(validations, validationNames.video),
      section: getSectionNameForValidation(validationNames.video)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.video));
  }
  if (isEmptyPractices) {
    missingFields.push({
      field: validationNames.specialties,
      displayName: getMissingFieldLabel(validations, validationNames.specialties),
      section: getSectionNameForValidation(validationNames.specialties)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.specialties));
  }
  if (isEmptyLanguages) {
    missingFields.push({
      field: validationNames.language,
      displayName: getMissingFieldLabel(validations, validationNames.language),
      section: getSectionNameForValidation(validationNames.language)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.language));
  }
  if (isEmptyWebsite) {
    missingFields.push({
      field: validationNames.website,
      displayName: getMissingFieldLabel(validations, validationNames.website),
      section: getSectionNameForValidation(validationNames.website)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.website));
  }
  if (isEmptySocations) {
    missingFields.push({
      field: validationNames.locations,
      displayName: getMissingFieldLabel(validations, validationNames.locations),
      section: getSectionNameForValidation(validationNames.locations)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.locations));
  }
  if (isEmptyCommunities) {
    missingFields.push({
      field: validationNames.communities,
      displayName: getMissingFieldLabel(validations, validationNames.communities),
      section: getSectionNameForValidation(validationNames.communities)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.communities));
  }
  if (isEmptyLicenses) {
    missingFields.push({
      field: validationNames.licenses,
      displayName: getMissingFieldLabel(validations, validationNames.licenses),
      section: getSectionNameForValidation(validationNames.licenses)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.licenses));
  }
  if (isEmptyEducations) {
    missingFields.push({
      field: validationNames.educations,
      displayName: getMissingFieldLabel(validations, validationNames.educations),
      section: getSectionNameForValidation(validationNames.educations)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.educations));
  }
  if (isEmptyAssociations) {
    missingFields.push({
      field: validationNames.associations,
      displayName: getMissingFieldLabel(validations, validationNames.associations),
      section: getSectionNameForValidation(validationNames.associations)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.associations));
  }
  if (isEmptySocialMedia) {
    missingFields.push({
      field: validationNames.socialMedia,
      displayName: getMissingFieldLabel(validations, validationNames.socialMedia),
      section: getSectionNameForValidation(validationNames.socialMedia)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.socialMedia));
  }
  if (isEmptyTags) {
    missingFields.push({
      field: validationNames.tags,
      displayName: getMissingFieldLabel(validations, validationNames.tags),
      section: getSectionNameForValidation(validationNames.tags)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.tags));
  }
  if (isEmptyJobs) {
    missingFields.push({
      field: validationNames.job,
      displayName: getMissingFieldLabel(validations, validationNames.job),
      section: getSectionNameForValidation(validationNames.job)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.job));
  }
  if (isEmptyBookMeetings) {
    missingFields.push({
      field: validationNames.calendar,
      displayName: getMissingFieldLabel(validations, validationNames.calendar),
      section: getSectionNameForValidation(validationNames.calendar)
    });
    missingSectionsSet.add(getSectionNameForValidation(validationNames.calendar));
  }

  const hasMissingFields = missingFields.length > 0;
  const missingSections = Array.from(missingSectionsSet);

  // Create result object that works as boolean but also contains details
  const result = {
    hasMissingFields,
    missingFields,
    missingSections
  };

  // Make it work in boolean contexts
  result.valueOf = () => hasMissingFields;
  result.toString = () => String(hasMissingFields);
  result[Symbol.toPrimitive] = () => hasMissingFields;

  return result;
};

export const getActiveStatuses = statusActive => {
  switch (statusActive) {
    case statusesConstantKeys.missingInformation:
      return statusesConstant.missingInformation;
    case statusesConstantKeys.matches:
      return statusesConstant.matches;
    case statusesConstantKeys.match:
      return statusesConstant.match;
    case statusesConstantKeys.notMatch:
      return statusesConstant.notMatch;
    case statusesConstantKeys.reviewed:
      return statusesConstant.reviewed;
    case statusesConstantKeys.rejected:
      return statusesConstant.rejected;
    case statusesConstantKeys.pendingReview:
      return statusesConstant.pendingReview;
    case statusesConstantKeys.requestReceived:
      return statusesConstant.requestReceived;
    default:
      return "";
  }
};

export const getActiveListings = listings => {
  return listings.filter(item => item.status === statuses.active);
};

export const getDraftListings = listings => {
  return listings.filter(item => item.status === statuses.draft);
};

export const getPendingListings = listings => {
  return listings.filter(item => item.status === statuses.pending);
};

export const getDataIntercom = listings => {
  const activeListings = getActiveListings(listings);

  if (!window.intercomSettings) {
    window.intercomSettings = {};
  }

  if (activeListings.length) {
    activeListings.slice(0, 5).forEach((item, index) => {
      const number = index + 1;
      const directoryUrl = `Current Directory ${number} URL`;
      const directoryName = `Current Directory ${number} Name`;

      window.intercomSettings[directoryUrl] = item.listing_url;
      window.intercomSettings[directoryName] = item.membership;
    });
  }

  window.intercomSettings["Active Listings"] = activeListings.length;
  window.intercomSettings["Draft Listings"] = getDraftListings(listings).length;
  window.intercomSettings["Pending Listings"] = getPendingListings(listings).length;
};

export const getResetPasswordError = status => {
  if (status === 400) {
    return "dashboard_forgotpassword_error_400";
  }

  if (status === 402) {
    return "dashboard_forgotpassword_error_402";
  }

  if (status === 403) {
    return "dashboard_forgotpassword_error_403";
  }

  if (status === 422) {
    return "dashboard_forgotpassword_error_422";
  }

  if (status >= 500) {
    return "dashboard_forgotpassword_error_500";
  }

  return "";
};

export const concatSimilarArray = array => {
  return array.reduce((accumulator, current) => {
    const activeIndex = accumulator.findIndex(item => item.title === current.title);
    if (activeIndex !== -1) {
      // A new row for the merge: the input rows are the action payload and must stay as the server sent them.
      const merged = { ...accumulator[activeIndex], leads: Number(accumulator[activeIndex].leads) + Number(current.leads) };
      return accumulator.map((item, index) => (index === activeIndex ? merged : item));
    }
    return [...accumulator, current];
  }, []);
};

export const formatPhoneNumber = phone => {
  const cleaned = `${phone}`.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3,4})(\d{3})(\d{4})$/);

  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  return null;
};

/**
 * Canonical E.164 for Add Recipient SMS, or null when the value cannot be sent.
 * Mirrors Hw_PhoneNormalizer (GROWTH-6523): strip ext tails; reject leftover
 * letters; keep a leading +; NANP 10-digit / 11-digit leading 1 become +1…;
 * anything else stays digits-only. Then require phoneE164 so junk like "1"
 * and "(505)505505" never reaches send-code.
 *
 * @param {unknown} raw
 * @returns {string|null}
 */
export const normalizePhone = raw => {
  if (raw == null) {
    return null;
  }

  let phone = String(raw).trim();
  if (!phone) {
    return null;
  }

  phone = phone.replace(/\s*(?:ext\.?|extension|x|#)\s*\d+\s*$/i, "");
  if (/[A-Za-z]/.test(phone)) {
    return null;
  }

  const hasPlus = phone.startsWith("+");
  const digits = phone.replace(/\D/g, "");
  if (!digits) {
    return null;
  }

  let canonical;
  if (hasPlus) {
    canonical = `+${digits}`;
  } else if (digits.length === 10) {
    canonical = `+1${digits}`;
  } else if (digits.length === 11 && digits[0] === "1") {
    canonical = `+${digits}`;
  } else {
    canonical = digits;
  }

  return phoneE164.test(canonical) ? canonical : null;
};

export const renderBrandIcon = brand => {
  switch (brand) {
    case "Visa":
      return visaIcon;
    case "American Express":
      return americanExprIcon;
    case "MasterCard":
      return masterCardBlack;
    case "Discover":
      return discoverIcon;
    default:
      return genericCardIcon;
  }
};

export const getInitialsListing = (itemToEdit = { attorney: "", membership: "" }) => {
  const [firstAttorney, secondAttorney] = (itemToEdit?.attorney || "").split(" ");
  const [firstMembership, secondMembership] = itemToEdit?.membership ? itemToEdit?.membership?.split(" ") : ["", ""];
  let initials = "";

  if (firstAttorney) {
    initials += firstAttorney[0];
  }

  if (secondAttorney) {
    initials += secondAttorney[0];
  }

  if (!firstAttorney && !secondAttorney && firstMembership && secondMembership) {
    initials += firstMembership[0] + secondMembership[0];
  }

  return initials;
};

export const isHaveNumber = string => {
  return /\d/.test(string);
};

export const CARD_STATUS = {
  OK: "ok",
  NO_CUSTOMER: "no_customer",
  ACCOUNT_DEFAULT: "account_default",
  MISSING: "missing"
};

export const normalizeSubscriptions = (subscriptions, cards, hasStripeCustomer = true) => {
  // The card's display fields ride on the row under the card's own keys: brand and last 4 for the row's card, and the expiry
  // and cardholder name that tell two same-last-4 cards apart (GROWTH-7348). A row whose card is not in `cards` has none.
  const normalizeCards = (cards || []).reduce((accumulator, current) => {
    accumulator[current.id] = {
      brand: current.brand,
      last4: current.last4,
      exp_month: current.exp_month,
      exp_year: current.exp_year,
      name: current.name
    };
    return accumulator;
  }, {});

  const getCardStatus = subscription => {
    if (normalizeCards[subscription.card_token]) {
      return CARD_STATUS.OK;
    }

    if (!hasStripeCustomer) {
      return CARD_STATUS.NO_CUSTOMER;
    }

    return subscription.card_token ? CARD_STATUS.MISSING : CARD_STATUS.ACCOUNT_DEFAULT;
  };

  return (subscriptions || []).map(subscription => {
    return {
      ...subscription,
      ...normalizeCards[subscription.card_token],
      card_status: getCardStatus(subscription)
    };
  });
};

export const getListOfYears = from => {
  const now = new Date().getUTCFullYear();

  return Array(now - (now - from || 1970))
    .fill("")
    .map((v, idx) => now - idx);
};

export const getCookie = name => {
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookiesArray = decodedCookie.split(";");
  for (let i = 0; i < cookiesArray.length; i++) {
    let cookie = cookiesArray[i];
    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(`${name}=`) === 0) {
      return cookie.substring(name.length + 1, cookie.length);
    }
  }
  return "";
};

export const getCountDaysInMonth = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

  return lastDayOfMonth.getDate();
};

export const createDaysArrayForCurrentMonth = () => {
  return Array.from({ length: getCountDaysInMonth() }, (_, index) => index);
};

export const isLastDayOfMonth = (date, day) => {
  const currentDate = new Date(date);
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const nextMonthFirstDay = new Date(currentYear, currentMonth + 1, 1);

  const lastDayOfMonth = new Date(nextMonthFirstDay - 1);

  return day === lastDayOfMonth.getDate();
};

export const getOccurrenceOfMonth = date => {
  const currentDate = new Date(date);
  const targetWeekday = new Date(date).getDay();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  let occurrence = 0;

  for (let day = 1; day <= new Date(year, month + 1, 0).getDate(); day++) {
    const currentDay = new Date(year, month, day);
    const currentWeekday = currentDay.getDay();

    if (currentWeekday === targetWeekday) {
      occurrence++;
      if (currentDay.getDate() === currentDate.getDate()) {
        return occurrence;
      }
    }
  }

  return -1;
};

export const createImageDeliveryUrl = imageUrl => `${imageDelivery}${imageUrl}`;

export const compareMsg = (a, b) => {
  return isBefore(new Date(a.sent), new Date(b.sent)) ? 1 : -1;
};

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height)
  };
}

export const getCoverCroppedImg = (imageSrc, crop, rotation, flip = { horizontal: false, vertical: false }) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;
    image.onload = () => {
      const targetWidth = 1200;
      const targetHeight = 300;

      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");

      const rotRad = getRadianAngle(rotation);
      const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

      tempCanvas.width = bBoxWidth;
      tempCanvas.height = bBoxHeight;

      tempCtx.translate(bBoxWidth / 2, bBoxHeight / 2);
      tempCtx.rotate(rotRad);
      tempCtx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
      tempCtx.translate(-image.width / 2, -image.height / 2);

      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = "high";

      tempCtx.drawImage(image, 0, 0);

      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = targetWidth;
      finalCanvas.height = targetHeight;
      const finalCtx = finalCanvas.getContext("2d");

      finalCtx.imageSmoothingEnabled = true;
      finalCtx.imageSmoothingQuality = "high";

      finalCtx.drawImage(tempCanvas, crop.x, crop.y, crop.width, crop.height, 0, 0, targetWidth, targetHeight);

      finalCanvas.toBlob(
        blob => {
          if (!blob) {
            console.error("Canvas is empty");
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        1.0 // Maximum quality (1.0 = 100%)
      );
    };
    image.onerror = () => reject(new Error("Failed to load image"));
  });
};

/**
 * Copy an input File into an in-memory File so the bytes survive input reset /
 * remount before the upload request is sent. Crop-modal uploads already use a
 * canvas Blob and are not affected.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export const snapshotImageFile = async file => {
  const buffer = await file.arrayBuffer();
  return new File([buffer], file.name || "image.jpg", { type: file.type || "image/jpeg" });
};

// Smallest logo / profile photo / parent-logo side the dashboard and apiv2 accept (GROWTH-7655).
export const MIN_LOGO_IMAGE_SIZE = 200;

/**
 * Resolves with {width, height} of an image data URL, or null when the browser cannot decode it or has no
 * HTMLImageElement.decode() (the server then makes the call).
 */
export const readImageDimensions = src =>
  new Promise(resolve => {
    const img = new Image();
    if (typeof img.decode !== "function") {
      resolve(null);
      return;
    }
    img.src = src;
    img.decode().then(
      () => resolve({ width: img.naturalWidth, height: img.naturalHeight }),
      () => resolve(null)
    );
  });

export const isBelowLogoMinimum = dimensions =>
  Boolean(dimensions) && (dimensions.width < MIN_LOGO_IMAGE_SIZE || dimensions.height < MIN_LOGO_IMAGE_SIZE);

export const getCroppedImg = (imageSrc, crop, rotation = 0, flip = { horizontal: false, vertical: false }) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const rotRad = getRadianAngle(rotation);
      const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

      canvas.width = bBoxWidth;
      canvas.height = bBoxHeight;

      ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
      ctx.rotate(rotRad);
      ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
      ctx.translate(-image.width / 2, -image.height / 2);
      ctx.drawImage(image, 0, 0);

      const croppedCanvas = document.createElement("canvas");
      croppedCanvas.width = crop.width;
      croppedCanvas.height = crop.height;
      const croppedCtx = croppedCanvas.getContext("2d");

      croppedCtx.drawImage(canvas, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

      croppedCanvas.toBlob(blob => {
        if (!blob) {
          console.error("Canvas is empty");
          return;
        }
        resolve(blob);
      }, "image/jpeg");
    };

    image.onerror = () => reject(new Error("Failed to load image"));
  });
};

export const sortBySponsorAndDate = items => {
  return items.sort((a, b) => {
    if (a.is_premium !== b.is_premium) {
      return a.is_premium ? -1 : 1;
    }
    return new Date(b.added) - new Date(a.added);
  });
};

export const normalizeMembershipInsurances = (insurances, insuranceGroups) => {
  return insurances?.reduce((total, insurance) => {
    const network = insuranceGroups.networks.find(item => String(insurance.network_id) === String(item.insurance_networks_id));
    const company = insuranceGroups.companies.find(item => String(insurance.company_id) === String(item.insurance_company_id));
    const plan = insuranceGroups.plans.find(item => String(insurance.plan_id) === String(item.insurance_plan_id));

    total.push({
      plan,
      network,
      company,
      other_plan: insurance.other_plan,
      other_company: insurance.other_company,
      other_network: insurance.other_network
    });

    return total;
  }, []);
};

/**
 * True when the given API datetime is less than two minutes old.
 * The backend writes datetimes in UTC ("YYYY-MM-DD HH:MM:SS", PHP runs with
 * date_default_timezone_set("UTC")), so the value is parsed as UTC and compared
 * with the real current instant. Missing, unparsable and future values are false.
 *
 * @param {string} date
 * @param {number} now epoch milliseconds, injectable for tests
 * @returns {boolean}
 */
export const isAddedLessThan2Minutes = (date, now = Date.now()) => {
  if (!date) {
    return false;
  }

  const value = String(date).trim();
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const addedTime = new Date(`${value.split(" ").join("T")}${hasZone ? "" : "Z"}`).getTime();
  if (Number.isNaN(addedTime)) {
    return false;
  }

  const diffMinutes = (now - addedTime) / 60000;

  // A small negative tolerance absorbs clock skew between server and browser.
  return diffMinutes > -2 && diffMinutes < 2;
};

// Utility function to decode HTML entities
export function decodeHtmlEntities(str) {
  if (!str) return "";
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}
